/**
 * Выгодные зоны: дёшево и дорого относительно недавней истории.
 *
 * Граница BUY-зоны — цена, ниже которой за окно торговались только
 * заданные проценты свечей. Не «поддержка» и не прогноз: утверждение о
 * том, где рынок бывал редко. Одиночный прокол весит одну свечу из
 * шестисот и зону не растягивает — в этом и смысл перцентиля вместо
 * минимума.
 *
 * Зон три на сторону — широкая, сильная, экстремальная, — и они
 * вложены. Под каждой ещё запас вниз по ATR: чем глубже RSI, тем больше
 * запас. Цена, ушедшая под самый край, уже не «дёшево», а «что-то
 * происходит», и зона это показывает толщиной, а не отдельной подписью.
 *
 * Перенесено из пайна «Выгодные зоны PRO». Настройки зашиты теми, что
 * стоят у автора: снаружи они не показываются, потому что это состав
 * индикатора, а не удобство. Открой их — и отдашь не настройку, а рецепт.
 */
import { atr, emaOfSparse, rsi } from './math.js';
/* ------------------------------------------------- зашитые настройки */
/** Окно выборки: относительно чего считается «дёшево». */
const LOOKBACK = 600;
/** Период сглаживания границ. Тип — EMA. */
const SMOOTH_LENGTH = 80;
/** Растягивает зону вверх от дна выборки. 1.0 — ровно на перцентиле. */
const ZONE_WIDTH = 0.7;
const BUY_OUTER = 32;
const BUY_STRONG = 20;
const BUY_EXTREME = 10;
const SELL_OUTER = 32;
const SELL_STRONG = 20;
const SELL_EXTREME = 10;
const ATR_LENGTH = 14;
const ATR_EXTENSION = 1.2;
const RSI_LENGTH = 14;
const RSI_BUY = 35;
const RSI_SELL = 65;
/**
 * Сколько баров нужно, чтобы зона появилась.
 *
 * Окно выборки плюс прогрев сглаживания: раньше этого граница считается
 * не по «редко», а по «всему, что успели увидеть».
 */
export const ZONE_WARMUP = LOOKBACK + SMOOTH_LENGTH;
/**
 * Прозрачность заливки по видам зон, как в исходнике.
 *
 * Настройка не задаёт прозрачность, а прибавляется к базовой: широкая
 * зона всегда бледнее сильной, сильная — экстремальной, и ручка двигает
 * всю тройку разом, сохраняя между ними разницу.
 */
export function zoneAlpha(base, opacity) {
    const transparency = Math.min(99, base + Math.round(((100 - base) * Math.min(100, Math.max(0, opacity))) / 100));
    return (100 - transparency) / 100;
}
/** Базовая прозрачность широкой, сильной и экстремальной зоны. */
export const ZONE_FADE = { outer: 90, strong: 78, extreme: 62 };
/**
 * Границы, по которым ранг цены окрашивается в табло.
 *
 * Те же числа, что строят зоны: ниже `cheap` — цена в дешёвой части
 * выборки, выше `rich` — в дорогой. Отдельные пороги «для показа»
 * означали бы, что цифра зелёная там, где зоны ещё нет.
 */
export const RANK_ZONES = { cheap: BUY_OUTER, rich: 100 - SELL_OUTER };
/** Таймфреймы, по которым показывается глубина. */
export const ZONE_TIMEFRAMES = [
    { label: '5м', timeframe: '5m', step: 5 * 60_000 },
    { label: '15м', timeframe: '15m', step: 15 * 60_000 },
    { label: '1ч', timeframe: '1H', step: 3_600_000 },
    { label: '4ч', timeframe: '4H', step: 4 * 3_600_000 },
];
/**
 * Что показывает табло — на один таймфрейм больше, чем считает счёт.
 *
 * Минутный ранг полезен глазу: по нему видно, докуда дошло движение
 * прямо сейчас. В счёт он не входит намеренно — минутный шум сдвигал бы
 * силу сигнала, а сигнал ставится не по минуте. Списки разные, и это
 * решение, а не расхождение.
 */
export const ZONE_READOUT_TIMEFRAMES = [
    { label: '1м', timeframe: '1m', step: 60_000 },
    ...ZONE_TIMEFRAMES,
];
/* ------------------------------------------------------------ расчёт */
/**
 * Перцентиль методом ближайшего ранга.
 *
 * Тот же метод, что у `ta.percentile_nearest_rank`: берётся реально
 * существовавшая цена, а не среднее между соседними. Интерполяция дала бы
 * границу на цене, которой не было, — мелочь, которая расходится с
 * исходником ровно там, где выборка редкая.
 */
export function percentileNearestRank(values, period, percent) {
    const out = new Array(values.length).fill(null);
    const size = Math.max(1, Math.floor(period));
    if (values.length < size)
        return out;
    /*
     * Окно поддерживается отсортированным, а не сортируется заново.
     *
     * Прямая сортировка на каждом баре — это шестьсот элементов на две
     * тысячи баров и семь границ зоны: десятки миллионов сравнений на
     * каждый пришедший тик. Считается это заметно дольше кадра, и график
     * начинает дёргаться ровно тогда, когда рынок оживает.
     */
    const sorted = [];
    const rank = Math.min(size - 1, Math.max(0, Math.ceil((percent / 100) * size) - 1));
    for (let i = 0; i < values.length; i += 1) {
        insert(sorted, values[i]);
        if (i >= size)
            remove(sorted, values[i - size]);
        if (i >= size - 1)
            out[i] = sorted[rank];
    }
    return out;
}
/** Место значения в отсортированном массиве. */
function lowerBound(sorted, value) {
    let low = 0;
    let high = sorted.length;
    while (low < high) {
        const mid = (low + high) >>> 1;
        if (sorted[mid] < value)
            low = mid + 1;
        else
            high = mid;
    }
    return low;
}
function insert(sorted, value) {
    sorted.splice(lowerBound(sorted, value), 0, value);
}
function remove(sorted, value) {
    const at = lowerBound(sorted, value);
    if (sorted[at] === value)
        sorted.splice(at, 1);
}
/**
 * Ранг цены в окне: какая доля прошлых свечей торговалась не выше её.
 *
 * Одно число вместо картинки, и потому единственное, чем зоны разных
 * таймфреймов можно честно сравнить между собой: сами границы у них в
 * разных ценах и сравнению не поддаются.
 */
export function percentRank(values, period) {
    const out = new Array(values.length).fill(null);
    const size = Math.max(1, Math.floor(period));
    for (let i = size; i < values.length; i += 1) {
        const current = values[i];
        let below = 0;
        for (let k = i - size; k < i; k += 1)
            if (values[k] <= current)
                below += 1;
        out[i] = (below / size) * 100;
    }
    return out;
}
/**
 * Границы зон по всему ряду.
 *
 * Считается разом, а не по бару: сглаживание рекурсивно, и посчитать
 * «значение на баре N» отдельно от предыдущих нельзя — вышло бы другое
 * число, чем на графике.
 */
export function zoneSeries(candles) {
    const highs = candles.map((candle) => candle.high);
    const lows = candles.map((candle) => candle.low);
    const closes = candles.map((candle) => candle.close);
    const atrSeries = atr(highs, lows, closes, ATR_LENGTH);
    const rsiSeries = rsi(closes, RSI_LENGTH);
    /*
     * Якоря — края локального диапазона.
     *
     * У автора вес старшего таймфрейма равен нулю, поэтому подмешивание
     * отключено и якорь совпадает с локальным краем. Оставлять мёртвую
     * ветку ради полноты значило бы держать код, который никогда не
     * исполняется и потому никогда не проверяется.
     */
    const anchorLow = rolling(lows, LOOKBACK, 'min');
    const anchorHigh = rolling(highs, LOOKBACK, 'max');
    const buyOuterLevel = percentileNearestRank(lows, LOOKBACK, BUY_OUTER);
    const buyStrongLevel = percentileNearestRank(lows, LOOKBACK, BUY_STRONG);
    const buyExtremeLevel = percentileNearestRank(lows, LOOKBACK, BUY_EXTREME);
    const sellOuterLevel = percentileNearestRank(highs, LOOKBACK, 100 - SELL_OUTER);
    const sellStrongLevel = percentileNearestRank(highs, LOOKBACK, 100 - SELL_STRONG);
    const sellExtremeLevel = percentileNearestRank(highs, LOOKBACK, 100 - SELL_EXTREME);
    // Отсчёт от дна выборки: так ручка «расширение зон» сохраняет смысл.
    const fromLow = (level) => level.map((value, i) => {
        const base = anchorLow[i];
        return value == null || base == null ? null : base + (value - base) * ZONE_WIDTH;
    });
    const fromHigh = (level) => level.map((value, i) => {
        const base = anchorHigh[i];
        return value == null || base == null ? null : base - (base - value) * ZONE_WIDTH;
    });
    const buyOuterTop = smooth(fromLow(buyOuterLevel));
    const buyStrongTop = smooth(fromLow(buyStrongLevel));
    const buyExtremeTop = smooth(fromLow(buyExtremeLevel));
    const sellOuterBottom = smooth(fromHigh(sellOuterLevel));
    const sellStrongBottom = smooth(fromHigh(sellStrongLevel));
    const sellExtremeBottom = smooth(fromHigh(sellExtremeLevel));
    // Запас под зоной: чем глубже RSI, тем он больше.
    const buyExtension = smooth(candles.map((_, i) => {
        const a = atrSeries[i];
        const r = rsiSeries[i];
        if (a == null || r == null)
            return null;
        return a * ATR_EXTENSION * (0.5 + Math.max(0, (RSI_BUY - r) / Math.max(RSI_BUY, 1)));
    }));
    const sellExtension = smooth(candles.map((_, i) => {
        const a = atrSeries[i];
        const r = rsiSeries[i];
        if (a == null || r == null)
            return null;
        return a * ATR_EXTENSION * (0.5 + Math.max(0, (r - RSI_SELL) / Math.max(100 - RSI_SELL, 1)));
    }));
    const zoneFloor = smooth(anchorLow);
    const zoneCeil = smooth(anchorHigh);
    // Граница не опускается ниже дна зоны и не поднимается выше потолка.
    const clampLow = (series) => series.map((value, i) => {
        const floor = zoneFloor[i];
        return value == null || floor == null ? null : Math.max(value, floor);
    });
    const clampHigh = (series) => series.map((value, i) => {
        const ceil = zoneCeil[i];
        return value == null || ceil == null ? null : Math.min(value, ceil);
    });
    const below = (share) => zoneFloor.map((floor, i) => {
        const extension = buyExtension[i];
        return floor == null || extension == null ? null : floor - extension * share;
    });
    const above = (share) => zoneCeil.map((ceil, i) => {
        const extension = sellExtension[i];
        return ceil == null || extension == null ? null : ceil + extension * share;
    });
    return {
        time: candles.map((candle) => candle.time),
        buyOuterTop: clampLow(buyOuterTop),
        buyStrongTop: clampLow(buyStrongTop),
        buyExtremeTop: clampLow(buyExtremeTop),
        buyOuterBottom: below(0.25),
        buyStrongBottom: below(0.55),
        buyExtremeBottom: below(1),
        sellOuterBottom: clampHigh(sellOuterBottom),
        sellStrongBottom: clampHigh(sellStrongBottom),
        sellExtremeBottom: clampHigh(sellExtremeBottom),
        sellOuterTop: above(0.25),
        sellStrongTop: above(0.55),
        sellExtremeTop: above(1),
        fairValue: smooth(percentileNearestRank(closes, LOOKBACK, 50)),
    };
}
/** Состояние зоны на последнем баре. */
export function zoneReading(candles, series) {
    const i = candles.length - 1;
    const candle = candles[i];
    if (!candle)
        return { label: 'Нет данных', side: 'none', depth: null };
    const at = (values) => values[i] ?? null;
    const buyOuterTop = at(series.buyOuterTop);
    const buyOuterBottom = at(series.buyOuterBottom);
    const buyStrongTop = at(series.buyStrongTop);
    const buyStrongBottom = at(series.buyStrongBottom);
    const buyExtremeTop = at(series.buyExtremeTop);
    const buyExtremeBottom = at(series.buyExtremeBottom);
    const sellOuterBottom = at(series.sellOuterBottom);
    const sellOuterTop = at(series.sellOuterTop);
    const sellStrongBottom = at(series.sellStrongBottom);
    const sellStrongTop = at(series.sellStrongTop);
    const sellExtremeBottom = at(series.sellExtremeBottom);
    const sellExtremeTop = at(series.sellExtremeTop);
    const inside = (low, high, value) => low !== null && high !== null && value >= low && value <= high;
    const inBuy = inside(buyOuterBottom, buyOuterTop, candle.low);
    const inBuyStrong = inside(buyStrongBottom, buyStrongTop, candle.low);
    const inBuyExtreme = inside(buyExtremeBottom, buyExtremeTop, candle.low);
    const inSell = inside(sellOuterBottom, sellOuterTop, candle.high);
    const inSellStrong = inside(sellStrongBottom, sellStrongTop, candle.high);
    const inSellExtreme = inside(sellExtremeBottom, sellExtremeTop, candle.high);
    const label = inBuyExtreme
        ? 'Глубоко под Лонг'
        : inBuyStrong
            ? 'Сильная под Лонг'
            : inBuy
                ? 'Под Лонг'
                : inSellExtreme
                    ? 'Глубоко под Шорт'
                    : inSellStrong
                        ? 'Сильная под Шорт'
                        : inSell
                            ? 'Под Шорт'
                            : 'Вне зоны';
    const side = inBuy ? 'buy' : inSell ? 'sell' : 'none';
    let depth = null;
    if (side === 'buy' && buyOuterTop !== null && buyExtremeBottom !== null) {
        const span = Math.max(buyOuterTop - buyExtremeBottom, Number.EPSILON);
        depth = clamp(((buyOuterTop - candle.close) / span) * 100);
    }
    else if (side === 'sell' && sellOuterBottom !== null && sellExtremeTop !== null) {
        const span = Math.max(sellExtremeTop - sellOuterBottom, Number.EPSILON);
        depth = clamp(((candle.close - sellOuterBottom) / span) * 100);
    }
    return { label, side, depth };
}
/**
 * Ранги по таймфреймам целыми рядами, а не одним значением.
 *
 * Табло смотрит на последнее число, а расчёт сигналов — на всю историю:
 * без ряда метка на прошлом баре считалась бы по сегодняшнему рангу, то
 * есть по будущему.
 */
export function timeframeRankFrames(candles, step, resample, fetched, 
/*
 * Список задаётся снаружи: у счёта он свой, у табло свой. По
 * умолчанию — тот, по которому считается сила, чтобы вызывающий
 * случайно не поменял её, попросив «ещё одну строку на экран».
 */
frames = ZONE_TIMEFRAMES) {
    return frames.map((frame) => {
        const own = frame.step === step ? candles : null;
        const loaded = own ?? fetched(frame.timeframe);
        const bars = loaded ?? (frame.step > step && step > 0 ? resample(candles, frame.step) : null);
        if (!bars) {
            return {
                label: frame.label,
                step: frame.step,
                times: [],
                ranks: [],
                missing: (frame.step < step ? 'younger' : 'short'),
            };
        }
        const ranks = percentRank(bars.map((bar) => bar.close), LOOKBACK);
        return {
            label: frame.label,
            step: frame.step,
            times: bars.map((bar) => bar.time),
            ranks,
            ...(ranks[ranks.length - 1] == null ? { missing: 'short' } : {}),
        };
    });
}
/**
 * Ранг цены по старшим таймфреймам.
 *
 * Ряд пересобирается из текущего, а не запрашивается отдельно: собрать
 * час из пятнадцатиминуток можно точно, обратное — нет. Таймфрейм младше
 * открытого честно возвращает `null`: выдумать его не из чего.
 */
export function timeframeRanks(candles, step, resample, fetched) {
    /*
     * Сначала ряд от Core, и только потом пересборка из открытого.
     *
     * Пересобрать можно лишь старший и лишь настолько, насколько хватает
     * загруженного: шестьсот четырёхчасовых баров — это девять тысяч
     * пятнадцатиминутных, которых на графике нет. Младший не собрать
     * вовсе. Оставить только пересборку значило бы показывать «мало
     * истории» там, где история есть, просто не у нас.
     */
    return timeframeRankFrames(candles, step, resample, fetched, ZONE_READOUT_TIMEFRAMES).map((frame) => {
        const rank = frame.ranks[frame.ranks.length - 1] ?? null;
        return rank === null
            ? { label: frame.label, rank: null, missing: frame.missing ?? 'short' }
            : { label: frame.label, rank };
    });
}
/* ---------------------------------------------------------- частное */
function clamp(value) {
    return Math.min(100, Math.max(0, value));
}
/** Скользящий минимум или максимум по окну. */
function rolling(values, period, wanted) {
    const out = new Array(values.length).fill(null);
    const size = Math.max(1, Math.floor(period));
    /*
     * Монотонная очередь индексов: голова всегда крайнее значение окна.
     * Перебор окна целиком дал бы то же самое и в шестьсот раз дороже.
     */
    const queue = [];
    const worse = (a, b) => (wanted === 'min' ? a >= b : a <= b);
    for (let i = 0; i < values.length; i += 1) {
        while (queue.length > 0 && worse(values[queue[queue.length - 1]], values[i]))
            queue.pop();
        queue.push(i);
        if (queue[0] <= i - size)
            queue.shift();
        if (i >= size - 1)
            out[i] = values[queue[0]];
    }
    return out;
}
/**
 * Сглаживание границы.
 *
 * Сырая граница — ступенчатая функция: перцентиль стоит на месте, пока в
 * окно не войдёт новое значение, а потом прыгает. Сглаживание размазывает
 * ступени ценой задержки, и это осознанный размен: зона показывает, где
 * рынок бывал редко, а не где он был минуту назад.
 *
 * Пропуски в начале ряда пропускаются, а не считаются нулями: ноль в
 * цене — не «нет данных», а «бесплатно».
 */
function smooth(values) {
    return emaOfSparse(values, SMOOTH_LENGTH);
}
/*
 * Прямой EMA отсюда больше не выводится: в общем пакете он уже есть в
 * `math.ts`, и второе имя для той же функции сделало бы вывоз пакета
 * двусмысленным.
 */
//# sourceMappingURL=zones.js.map